import { CronExpressionParser } from 'cron-parser';

// The product's cron fields are always a 5-field schedule (minute hour
// day-of-month month day-of-week) or an `@`-alias. 5.10.0 (#443) pads a
// shorter expression from the *leading* field slot instead of rejecting it,
// so `'0'` now parses as `'* * * * 0'` -- every minute, all day Sunday --
// instead of the midnight a merchant typing a bare `0` almost certainly
// meant, and `'*'` / `'* *'` similarly become "every minute, always" with no
// error shown. Reject anything shorter than 5 fields up front, before
// cron-parser gets a chance to pad it, so the accept/reject line for these
// inputs stays where it was on 5.6.2.
const MIN_CRON_FIELDS = 5;

const hasTooFewFields = (value) => {
  const trimmed = value.trim();
  if (trimmed.startsWith('@')) {
    // A single alias token (`@daily`, etc.) -- cron-parser itself rejects an
    // unknown or unsupported alias (e.g. `@reboot`), so no extra check needed.
    return false;
  }
  return trimmed.split(/\s+/).length < MIN_CRON_FIELDS;
};

// Whether the cron field should show its "invalid expression" error. This only
// asks cron-parser to parse -- it deliberately does not walk the schedule to a
// first occurrence, because it runs on every keystroke and giving up on an
// unsatisfiable expression is slow: parsing and walking an expression with no
// reachable occurrence (e.g. `'0 0 31 2,4,6 *'`) measured ~25ms per call on
// this machine (100 calls took ~2.6s, individual calls ranging ~24-85ms),
// against well under 1ms for an ordinary schedule like `'0 0 * * *'`.
// nextCronRun below is where that second failure mode is handled.
const isInvalidCronExpression = (value) => {
  if (!value) {
    return false;
  }

  if (hasTooFewFields(value)) {
    return true;
  }

  try {
    CronExpressionParser.parse(value);
    return false;
  } catch (e) {
    console.warn(e);
    return true;
  }
};

// Next fire time for a cron expression, evaluated under UTC (the timezone the
// backend scheduler runs in). Returns a string in the browser's local timezone,
// or null if the expression is empty, too short, unparseable, or never actually
// fires. Keep the UTC evaluation here so the "Next sync" display can't drift
// apart from isInvalidCronExpression again.
//
// The `hasTooFewFields` check has to be repeated here, not just in
// isInvalidCronExpression: without it, a bare `'0'` -- which the field flags as
// invalid -- would still parse (cron-parser pads it to `'* * * * 0'`, see
// hasTooFewFields above) and this would return a real next-run date, rendering
// a "Next sync" line under an input that's showing an error.
//
// `.next()` has to be inside the try, not just `parse()`. An expression can be
// syntactically valid and still have no next occurrence -- `0 0 31 2,4,6 *` asks
// for the 31st of February, April and June -- and cron-parser accepts that at
// parse time, then throws "loop limit exceeded" when asked to walk to it.
// NextSyncLabel renders this inline, so a throw escaping here blanks the whole
// settings tab; returning null just drops the "Next sync" line.
export const nextCronRun = (value) => {
  if (!value || hasTooFewFields(value)) {
    return null;
  }

  try {
    return CronExpressionParser.parse(value, { tz: 'UTC' }).next().toString();
  } catch (e) {
    console.warn(e);
    return null;
  }
};

export default isInvalidCronExpression;
